/**
 * Document eligibility gate.
 *
 * Implements C-13 … C-16 from docs/compliance-research.md. Every BLOCK below
 * traces to a statute; the citation travels with the rule so the UI can show the
 * reader *why*, and so nobody later "tidies up" a category without reading the
 * authority.
 *
 * Design decision (requirements.md § 1.1): blocked categories are BLOCKED, not
 * warned. A dismissible warning is not protection against a mistake made in a
 * bad mood at 11pm, and the failure mode here is not a bad user experience — it
 * is an unlawful detainer that fails at the courthouse.
 */

export type Eligibility = 'allowed' | 'warn' | 'block';

export interface DocumentCategory {
  id: string;
  label: string;
  eligibility: Eligibility;
  /** Shown to the owner. Plain language first, citation second. */
  reason?: string;
  /** Statutory authority, rendered verbatim in the UI and stored on the envelope. */
  authority?: string;
}

export const DOCUMENT_CATEGORIES: DocumentCategory[] = [
  // ── Allowed ────────────────────────────────────────────────────────────────
  {
    id: 'contractor_agreement',
    label: 'Contractor or vendor agreement',
    eligibility: 'allowed',
  },
  {
    id: 'lease_new',
    label: 'Lease or rental agreement (new tenancy)',
    eligibility: 'warn',
    reason:
      'Signing a new lease electronically is fine. But statutory notices during a tenancy — ' +
      'pay-or-quit, notice to quit, or anything served under Code of Civil Procedure § 1162 — ' +
      'cannot be sent electronically. Use the "Statutory notice" category for those; it is blocked.',
    authority: 'Cal. Civ. Code § 1633.3(c); ESIGN 15 U.S.C. § 7003(b)(2)(B)',
  },
  { id: 'nda', label: 'NDA or confidentiality agreement', eligibility: 'allowed' },
  { id: 'business_llc', label: 'LLC or business document', eligibility: 'allowed' },
  { id: 'consulting', label: 'Consulting or services agreement', eligibility: 'allowed' },
  { id: 'equipment_rental', label: 'Equipment or vehicle rental agreement', eligibility: 'allowed' },
  { id: 'household', label: 'Family or household paperwork (non-legal)', eligibility: 'allowed' },
  { id: 'other', label: 'Other', eligibility: 'allowed' },

  // ── Warn ───────────────────────────────────────────────────────────────────
  {
    id: 'real_property',
    label: 'Real property document (purchase, sale, deed, lien)',
    eligibility: 'warn',
    reason:
      'You can sign this electronically, but BeckerSign output CANNOT be recorded with a county ' +
      'recorder. Recording requires original signatures, and electronic submission is limited to ' +
      'title insurers, underwritten title companies, institutional lenders and government ' +
      'entities — you are not an authorized submitter. Anything destined for the recorder ' +
      '(grant deed, deed of trust, reconveyance, mechanics lien, memorandum of lease) must be ' +
      'wet-signed.',
    authority: 'Cal. Gov. Code § 27201(b); Electronic Recording Delivery Act, Gov. Code § 27391',
  },
  {
    id: 'trust_poa',
    label: 'Living trust, power of attorney, or advance health care directive',
    eligibility: 'warn',
    reason:
      'Genuinely unsettled, and BeckerSign is the wrong tool for it. ESIGN excludes *testamentary* ' +
      'trusts by name; an inter vivos trust is arguably outside the literal exclusion. But ' +
      'execution formalities, witness requirements and notarization practice all cut against ' +
      'doing this electronically, and New York excludes disposition-on-incompetence documents ' +
      'outright. Talk to an estate attorney before relying on this.',
    authority:
      'ESIGN 15 U.S.C. § 7003(a)(1); Cal. Civ. Code § 1633.3(b)(1); N.Y. State Tech. Law § 307(1)',
  },

  // ── Block ──────────────────────────────────────────────────────────────────
  {
    id: 'statutory_notice',
    label: 'Statutory notice (eviction, default, foreclosure, repossession, utility, insurance)',
    eligibility: 'block',
    reason:
      'California expressly forbids substituting an electronic record for these notices. Serving ' +
      'a pay-or-quit or notice to quit through BeckerSign would likely make your unlawful ' +
      'detainer defective from the start — and you would find out at the courthouse. Serve these ' +
      'on paper by a legally sufficient method.',
    authority:
      'Cal. Civ. Code § 1633.3(c) (barring substitution for Code Civ. Proc. § 1162 notices, ' +
      '§§ 2924b/c/f/i/j, 2924.3, § 1950.6, ch. 2.5 commencing § 1695); ' +
      'ESIGN 15 U.S.C. § 7003(b)(2)(A)–(C)',
  },
  {
    id: 'will_trust_testamentary',
    label: 'Will, codicil, or testamentary trust',
    eligibility: 'block',
    reason:
      'Excluded from ESIGN and from California’s UETA. Wills have their own execution ' +
      'formalities — witnesses, and in some cases notarization — that an e-signature platform ' +
      'does not satisfy. See an estate attorney.',
    authority: 'ESIGN 15 U.S.C. § 7003(a)(1); Cal. Civ. Code § 1633.3(b)(1)',
  },
  {
    id: 'family_law',
    label: 'Family law (divorce, adoption, custody)',
    eligibility: 'block',
    reason:
      'ESIGN does not apply to state law governing adoption, divorce, or other matters of family ' +
      'law. The exclusion is written broadly and covers more than the three named categories.',
    authority: 'ESIGN 15 U.S.C. § 7003(a)(2)',
  },
  {
    id: 'court_filing',
    label: 'Court filing, pleading, or order',
    eligibility: 'block',
    reason:
      'Court orders, notices and official court documents are excluded from ESIGN. Courts that ' +
      'accept e-filing do so under their own rules and through their own systems — that is the ' +
      'court’s authority, not something BeckerSign can supply.',
    authority: 'ESIGN 15 U.S.C. § 7003(b)(1)',
  },
  {
    id: 'negotiable_instrument',
    label: 'Negotiable instrument or document of title',
    eligibility: 'block',
    reason:
      'Instruments where possession confers title cannot be handled as ordinary electronic ' +
      'records — a perfect copy would be indistinguishable from the original, which is the whole ' +
      'problem. California excludes UCC Divisions 3, 4, 5, 8, 9 and 11.',
    authority: 'Cal. Civ. Code § 1633.3(b)(2)–(3); ESIGN 15 U.S.C. § 7003(a)(3)',
  },
  {
    id: 'hazmat',
    label: 'Document accompanying hazardous materials',
    eligibility: 'block',
    reason:
      'Any document required to accompany the transportation or handling of hazardous materials, ' +
      'pesticides, or other toxic or dangerous materials is excluded from ESIGN.',
    authority: 'ESIGN 15 U.S.C. § 7003(b)(3)',
  },
  {
    id: 'product_recall',
    label: 'Product recall or safety notice',
    eligibility: 'block',
    reason:
      'Recall notices and notices of material failure affecting health or safety are excluded ' +
      'from ESIGN.',
    authority: 'ESIGN 15 U.S.C. § 7003(b)(2)(D)',
  },
];

export function getCategory(id: string | undefined | null): DocumentCategory | undefined {
  return DOCUMENT_CATEGORIES.find((c) => c.id === id);
}

export function isBlocked(id: string | undefined | null): boolean {
  return getCategory(id)?.eligibility === 'block';
}

/**
 * Phrases that suggest the document needs a notary (C-15).
 *
 * Advisory only — never blocking. A false positive here costs one dismissed
 * warning; missing one costs a document that cannot be used for its purpose.
 */
const NOTARY_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /\bnotary public\b/i, label: '"notary public"' },
  { re: /\backnowledg(?:ment|ement)\b/i, label: '"acknowledgment"' },
  { re: /\bjurat\b/i, label: '"jurat"' },
  { re: /\bsubscribed and sworn\b/i, label: '"subscribed and sworn"' },
  { re: /\bwitness my hand and official seal\b/i, label: '"witness my hand and official seal"' },
  { re: /\bbefore me,?\s+(?:the undersigned|a notary)\b/i, label: '"before me, a notary"' },
];

export interface NotaryScan { needsNotary: boolean; matches: string[] }

export function scanForNotarization(text: string): NotaryScan {
  const matches = NOTARY_PATTERNS.filter((p) => p.re.test(text)).map((p) => p.label);
  return { needsNotary: matches.length > 0, matches };
}
